// ABOUTME: REST hook for the read-filter preview (POST /api/visibility-filters/object-schemas/:id/explain).
// ABOUTME: PIM_SUPERADMIN only server-side; read-only: it saves nothing, it counts and EXPLAINs the expression.

import { useCallback, useRef, useState } from 'react';

import { errorMessageOf, ExplainResult, parseExplainResult } from '../utils/explainPlan';

export function useExplainReadFilter(objectSchemaId: string) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [result, setResult] = useState<ExplainResult | null>(null);
    // Bumped by every explain and reset: an answer that is no longer the latest request is ignored.
    const requestId = useRef(0);

    const explain = useCallback(
        async (expression: string, timeoutMs?: number) => {
            requestId.current += 1;
            const id = requestId.current;
            const isCurrent = () => requestId.current === id;
            setLoading(true);
            setError(null);
            setResult(null);
            try {
                const response = await fetch(
                    `/api/visibility-filters/object-schemas/${encodeURIComponent(
                        objectSchemaId,
                    )}/explain`,
                    {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ expression, ...(timeoutMs ? { timeoutMs } : {}) }),
                    },
                );
                const payload: unknown = await response.json().catch(() => null);
                if (!isCurrent()) {
                    return;
                }
                const parsed = response.ok ? parseExplainResult(payload) : undefined;
                if (!parsed) {
                    setError(errorMessageOf(payload, `Explain failed (${response.status})`));
                    return;
                }
                setResult(parsed);
            } catch (err) {
                if (isCurrent()) {
                    setError(err instanceof Error ? err.message : 'Explain failed');
                }
            } finally {
                if (isCurrent()) {
                    setLoading(false);
                }
            }
        },
        [objectSchemaId],
    );

    const reset = useCallback(() => {
        requestId.current += 1;
        setLoading(false);
        setError(null);
        setResult(null);
    }, []);

    return { loading, error, result, explain, reset };
}
